// Orchestrates the two-step delivery-creation flow:
//   1. POST /ord/delivery_note  with the array of order ids
//        → returns the created delivery notes
//   2. For each created DN, POST /ord/delivery-note/:id/source-warehouse
//        to record the chosen warehouse on the dn_classification sidecar
//
// If step (1) succeeds and step (2) fails for any DN, we surface a partial
// error to the caller and let the manager retry the assignment from the
// "Хүлээлтэнд" tab. This keeps the slice idempotent: a re-run of the modal
// won't double-create DNs because the order will no longer be AUTHORIZED.

import { useState } from "react";
import { DeliveryApi } from "../../../apis";

interface ICreateDeliveryResult {
  createdCount: number;
  warehouseAssigned: number;
  failures: Array<{ deliveryNoteId: string; reason: string }>;
}

interface IDeliveryNoteResp {
  id: string;
}

export const useCreateDelivery = () => {
  const [submitting, setSubmitting] = useState(false);

  const submit = async (
    orderIds: string[],
    warehouseId: string | null,
  ): Promise<ICreateDeliveryResult> => {
    setSubmitting(true);
    try {
      const created = (await DeliveryApi.create(orderIds)) as IDeliveryNoteResp[] | { rows: IDeliveryNoteResp[] };
      const dnList: IDeliveryNoteResp[] = Array.isArray(created)
        ? created
        : (created?.rows ?? []);

      if (!warehouseId) {
        return { createdCount: dnList.length, warehouseAssigned: 0, failures: [] };
      }

      const failures: Array<{ deliveryNoteId: string; reason: string }> = [];
      let assigned = 0;
      for (const dn of dnList) {
        try {
          await DeliveryApi.setSourceWarehouse(dn.id, warehouseId);
          assigned += 1;
        } catch (err) {
          failures.push({ deliveryNoteId: dn.id, reason: (err as Error).message ?? "unknown" });
        }
      }
      return { createdCount: dnList.length, warehouseAssigned: assigned, failures };
    } finally {
      setSubmitting(false);
    }
  };

  return { submit, submitting };
};
